import React, { ReactNode, forwardRef } from "react";

import classnames from "classnames";
import { twMerge } from "tailwind-merge";

export type BadgeV2Color = "neutral" | "primary" | "secondary" | "accent" | "info" | "success" | "warning" | "error";
export type BadgeV2Size = "xs" | "sm" | "md" | "lg" | "xl";
export type BadgeV2Variant = "outline" | "dash" | "soft" | "ghost";

export interface BadgeV2Props extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  children?: ReactNode;
  color?: BadgeV2Color;
  size?: BadgeV2Size;
  variant?: BadgeV2Variant;
}

const colorClasses: Record<BadgeV2Color, string> = {
  neutral: "si-badge-neutral",
  primary: "si-badge-primary",
  secondary: "si-badge-secondary",
  accent: "si-badge-accent",
  info: "si-badge-info",
  success: "si-badge-success",
  warning: "si-badge-warning",
  error: "si-badge-error",
};

const sizeClasses: Record<BadgeV2Size, string> = {
  xs: "si-badge-xs",
  sm: "si-badge-sm",
  md: "si-badge-md",
  lg: "si-badge-lg",
  xl: "si-badge-xl",
};

const variantClasses: Record<BadgeV2Variant, string> = {
  outline: "si-badge-outline",
  dash: "si-badge-dash",
  soft: "si-badge-soft",
  ghost: "si-badge-ghost",
};

/**
 * Cobalt BadgeV2 - Based on DaisyUI badge classes
 * @see https://daisyui.com/components/badge/
 */
export const BadgeV2 = forwardRef<HTMLSpanElement, BadgeV2Props>(({ children, className, color, size = "md", variant, ...rest }, ref) => {
  const classes = twMerge("si-badge", sizeClasses[size], classnames(color && colorClasses[color], variant && variantClasses[variant]), className);

  return (
    <span ref={ref} className={classes} {...rest}>
      {children}
    </span>
  );
});

BadgeV2.displayName = "BadgeV2";

export default BadgeV2;
